import React from 'react';
import './analyticsAsk.css';

// Human-test feedback (session #113): 'Directional' read as jargon — badge copy stays in
// data-amount language. The contract tier name is still 'directional' server-side.
const BADGE_LABEL = { green: 'Solid', amber: 'Limited data', red: 'Not enough data yet' };
const TREND_GLYPH = { up: '↑', flat: '→', down: '↓' };

/** Short date, matching the codebase's dominant idiom (ReceivedRecognitions/DigestPreferences/
 *  NotableActivityFeed all use toLocaleDateString(undefined, {month:'short', day:'numeric'})).
 *  Null-safe: a missing/unparseable timestamp yields null (the segment is simply omitted). */
function formatShortDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** Secondary context line for a census row's `extra` (stuck_learners — stuckLearners.js emits
 *  {trend, most_attempted_scenario_name, last_run_at}). The most-attempted scenario is the
 *  actionable part (StrugglingLearnersService's research: sub-0.40 runs concentrate in a couple
 *  of scenarios, so a facilitator needs WHICH scenario the learner is stuck on), plus a trend
 *  glyph (↑/→/↓ for StrugglingLearnersService's 'up'/'flat'/'down') and a short last-active date.
 *  Every sub-field is optional — segments are independently omitted, and an all-null extra yields
 *  null (no secondary line at all). */
function stuckRowContext(extra) {
  if (!extra) return null;
  const parts = [];
  if (extra.most_attempted_scenario_name) parts.push(`Stuck on: ${extra.most_attempted_scenario_name}`);
  if (TREND_GLYPH[extra.trend]) parts.push(TREND_GLYPH[extra.trend]);
  const lastActive = formatShortDate(extra.last_run_at);
  if (lastActive) parts.push(`last active ${lastActive}`);
  return parts.length ? parts.join(' · ') : null;
}

/** Secondary context line for a PRE-RANKED learner row's `context` (tableContract.js's Task 6/
 *  #1666b pass-through, forwarded onto the presentation by Task 11 — `{attempts, last_active}`).
 *  Mirrors stuckRowContext's muted-secondary-line convention above for the learner-ranking
 *  table's own shape (a DIFFERENT shape from the census row's `extra` — the two never co-occur
 *  on the same row). Every sub-field is independently optional and null-safe, same discipline as
 *  stuckRowContext, even though the runner guarantees both are populated in practice (HAVING
 *  ensures >=1 eligible score) — defensive against a malformed payload. */
function rankedRowContext(context) {
  if (!context) return null;
  const parts = [];
  if (typeof context.attempts === 'number') parts.push(`${context.attempts} attempt${context.attempts === 1 ? '' : 's'}`);
  const lastActive = formatShortDate(context.last_active);
  if (lastActive) parts.push(`last active ${lastActive}`);
  return parts.length ? parts.join(' · ') : null;
}

/** Pure render of a `table` presentation (tableContract.js's presentTable output) — the
 *  scenario-grain / template-census sibling of AnswerCard. Renders server-formatted fields
 *  verbatim — never recomputes a value, a rank, or a badge.
 *
 *  Field order mirrors presentTable's own module-header convention:
 *   - `title` (r3, 2026-07-04) — short server-authored header (registry METRIC_LABELS), the
 *     card's first element, so a chip-launched answer never reads as a headerless grid.
 *   - `disclosure` (census templates only, e.g. stuck_learners' fixed 14d definition) sits
 *     under the title, above the headline — the definition a caller needs to trust the list is not a detail
 *     tucked behind an expander (Decision 8, #1660-follow-on).
 *   - `headline` (template-ranking metrics only, e.g. hardest_scenario's floor-passing pick) —
 *     null for a plain per-scenario table (Task 2) and for a census template (Task 6, no ranking
 *     concept) alike; both render fine with no headline block at all.
 *   - `scope_line` is ALWAYS rendered (Decision 8: never hidden behind an expander) — unlike
 *     headline, this is present on every table kind.
 *   - `presets` (`[{key,label,body,active}]|null`, #1694 Task 9) — server-authored definition
 *     toggles for a template-table answer, rendered directly UNDER the scope line (human-test
 *     2026-07-04: at the bottom of the card they were easily overlooked — up top it's clear the
 *     definition is adjustable BEFORE the reader digests the table). Own callback,
 *     `onPresetChange(chip)` — NEVER `onFollowUp` — because a preset is a card-local definition
 *     toggle, not a new question (gemini plan-review P1.3/P2.4). The active preset renders
 *     disabled + `aa-chip--active` (existing filled-chip style). null for every non-template
 *     table → no row at all.
 *   - rows: label / per-row badge / value / n / ci_label. `warn` is boolean `true` only for a
 *     suppressed SCALAR row (badge red — the row-tint carries the styling; census rows send
 *     warn:false since human-test 2026-07-04, the n column carries thin-data honesty). The ⚠️
 *     glyph branch below is retained defensively for any future non-red warn value. A census
 *     row's `extra` context (which scenario the learner is stuck on / trend / last active — see
 *     stuckRowContext above) renders as a muted secondary line under the label. A pre-ranked
 *     learner-table row's `context` (Task 6/#1666b's `{attempts, last_active}`, forwarded onto the
 *     presentation by Task 11 — the two context shapes never co-occur on one row) renders the
 *     same way via rankedRowContext, mirroring the census convention rather than inventing a new one.
 *   - `footnote` and the `follow_up` chip (tapping it calls `onFollowUp(follow_up)` — the WHOLE
 *     `{label, body}` chip, #1692, so the caller's breadcrumb can use the server-authored label
 *     instead of re-deriving one; `body` is a ready-to-POST /metric shape, e.g. hardest_scenario's
 *     "Rank by completion rate instead"). */
export default function TableCard({ presentation: p, onFollowUp, onPresetChange }) {
  if (!p) return null;
  return (
    <div className={`aa-table${p.badge ? ` aa-table--${p.badge}` : ''}`}>
      {p.title && <h3 className="aa-table__title">{p.title}</h3>}
      {p.disclosure && <p className="aa-table__disclosure">{p.disclosure}</p>}
      {p.headline && (
        <div className="aa-table__headline-row">
          <span className={`aa-badge aa-badge--${p.headline.badge}`}>
            <span className="aa-badge__dot" />{BADGE_LABEL[p.headline.badge] || p.headline.badge}
          </span>
          <div className="aa-table__headline">{p.headline.label} — {p.headline.value_label}{typeof p.headline.n === 'number' && <span className="aa-table__headline-n"> · n={p.headline.n}</span>}</div>
        </div>
      )}
      <p className="aa-table__scope">{p.scope_line}</p>
      {p.presets && (
        <div className="aa-table__presets" role="group" aria-label="Definition presets">
          {p.presets.map((c) => (
            <button key={c.key} type="button" disabled={c.active} aria-pressed={c.active}
              className={`aa-chip aa-chip--preset${c.active ? ' aa-chip--active' : ''}`}
              onClick={() => onPresetChange?.(c)}>
              {c.label}
            </button>
          ))}
        </div>
      )}
      <div className="aa-table__scroll">
        <table className="aa-table__grid">
          <thead>
            <tr><th>Label</th><th>Badge</th><th>Value</th><th>n</th><th>Range</th></tr>
          </thead>
          <tbody>
            {p.rows.map((r, i) => {
              const extraLine = stuckRowContext(r.extra);
              const contextLine = rankedRowContext(r.context);
              return (
              <tr key={i} className={r.warn ? 'aa-table__row--warn' : undefined}>
                <td className="aa-table__label">
                  {r.warn && r.badge !== 'red' && <span className="aa-table__warn-glyph" aria-hidden="true">⚠️ </span>}
                  {r.label}
                  {extraLine && <span className="aa-table__extra">{extraLine}</span>}
                  {contextLine && <span className="aa-table__extra">{contextLine}</span>}
                </td>
                <td>
                  <span className={`aa-badge aa-badge--${r.badge}`}>
                    <span className="aa-badge__dot" />{BADGE_LABEL[r.badge] || r.badge}
                  </span>
                </td>
                <td>{r.value_label}</td>
                <td>{r.n}</td>
                <td>{r.ci_label}</td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {p.footnote && <p className="aa-table__footnote">{p.footnote}</p>}
      {p.follow_up && (
        <button type="button" className="aa-chip aa-chip--followup" onClick={() => onFollowUp?.(p.follow_up)}>
          {p.follow_up.label}
        </button>
      )}
      {/* Fix 4 (human-test round 1, #1666): server-authored ranking follow-up chips — the SAME
          {label, body} shape as the single `follow_up` chip above (tableContract.js's
          buildRankingFollowUpChips), just an ARRAY (three re-rank variants) instead of one.
          Wired through the exact same onFollowUp affordance/handler — a ranking follow-up tap is
          not conceptually different from a metric follow-up tap, both replay a server-authored,
          already-verified /metric body. null for a plain scenario table or a census template
          (stuck_learners has no ranking concept) — no row at all. */}
      {p.ranking_follow_ups && (
        <div className="aa-table__ranking-followups" role="group" aria-label="Re-rank this list">
          {p.ranking_follow_ups.map((chip) => (
            <button key={chip.label} type="button" className="aa-chip aa-chip--followup"
              onClick={() => onFollowUp?.(chip)}>
              {chip.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
